import type { PropsWithChildren, ReactNode } from 'react';
import { useCallback, createContext, useContext } from 'react';

import type { FeatureList, FeatureName } from '@models';

export const EntitlementsContext = createContext<Partial<FeatureList>>({});

interface EntitlementsProviderProps {
  entitlements: Partial<FeatureList>;
}

export const EntitlementsProvider = ({
  children,
  entitlements = {},
}: PropsWithChildren<EntitlementsProviderProps>) => (
  <EntitlementsContext.Provider value={entitlements}>
    {children}
  </EntitlementsContext.Provider>
);

/**
 * Checks whether a given feature entitlement is enabled. Returns true for a
 * falsy feature name, which is assumed to mean that no entitlement is required.
 *
 * In React, prefer the {@link useEntitlement} hook instead, which calls this
 * internally. This function is provided for use outside of React, to ensure we
 * apply the same logic.
 */
export const hasFeatureEntitlement = (
  entitlements: Partial<FeatureList>,
  name?: FeatureName,
) => {
  // Handle possible falsy name here to avoid unnecessary logic in consumers
  // i.e. undefined implies that an entitlement is not required
  if (!name) return true;

  return name in entitlements && entitlements[name] === true;
};

export const useEntitlements = () => {
  const entitlements = useContext(EntitlementsContext);

  const isEnabled = useCallback(
    (name?: FeatureName) => hasFeatureEntitlement(entitlements, name),
    [entitlements],
  );

  return {
    entitlements,
    hasFeatureEntitlement: isEnabled,
  };
};

/**
 * Returns a boolean indicating whether a given entitlement is enabled
 */
export const useEntitlement = (name?: FeatureName) =>
  useEntitlements().hasFeatureEntitlement(name);

export interface EntitlementProps {
  name: FeatureName;
  then: ReactNode;
  else?: ReactNode;
}

/**
 * Conditionally render components based on the state of a given entitlement
 */
export const Entitlements = ({
  name,
  then: thenExpr,
  else: elseExpr = null,
}: EntitlementProps) => {
  const hasFeature = useEntitlement(name);

  return <>{hasFeature ? thenExpr : elseExpr}</>;
};

export default Entitlements;
